import React, { useState } from 'react';
import { DiscountPrice } from '@common/components/Price';
import { useTranslations } from '@lang';
import { ChevronUpIcon, ChevronDownIcon } from '@root/styles/icons';
import { Link } from 'shared-routing';
import { Discount, Price as PriceType } from 'shared-types/Common';
import { Thumbnail } from './Thumbnail';
import {
  BasketItemWrapper,
  BasketItemThumbnailColumn,
  BasketItemDetailsColumn,
  BasketItemRow,
  BasketItemDetailsRow,
  BasketItemColumn,
  BasketItemTitle,
  BasketItemRemoveButton,
  BasketItemPrice,
  MetaWrapper,
  IconWrapper,
  BasketItemDoubleColumn,
  BasketItemPriceWrapper,
  BasketItemPricePerNightWrapper,
} from './BasketItem.styles';
import { analytics, formatPrice } from '@features/analytics';

type BasketItemProps = {
  name: string;
  thumbnail: string | null;
  isEditable: boolean;
  price?: PriceType | null;
  shouldRenderMobileDetails?: boolean;
  linkRoute?: string;
  linkParams?: object;
  onRemove: () => void;
  renderDetails?: JSX.Element;
  renderMetadata?: () => React.ReactNode;
  discount?: Discount;
  pricePerNight?: JSX.Element | null;
  hidePrice?: boolean;
};

export const BasketItem: React.FC<BasketItemProps> = ({
  name,
  price,
  discount,
  thumbnail,
  onRemove,
  renderDetails,
  renderMetadata,
  linkRoute,
  isEditable = false,
  linkParams = {},
  shouldRenderMobileDetails = true,
  pricePerNight = null,
  hidePrice,
}) => {
  const { t } = useTranslations();
  const [isDetailsVisible, toggleDetails] = useState(false);
  const metadata = renderMetadata && renderMetadata();

  function handleRemoveClick(event) {
    event.preventDefault();

    if (price) {
      analytics.track({
        event: 'remove_from_cart',
        params: {
          currency: price.currencyCode,
          value: formatPrice(price.amount),
          items: [
            {
              item_name: name,
              currency: price.currencyCode,
              price: formatPrice(price.amount),
            },
          ],
        },
      });
    }

    onRemove();
  }

  function handleToggleDetails(event) {
    event.preventDefault();

    toggleDetails(!isDetailsVisible);
  }

  return (
    <BasketItemWrapper>
      <BasketItemRow>
        <BasketItemThumbnailColumn>
          {linkRoute ? (
            <Link route={linkRoute} params={linkParams}>
              <Thumbnail url={thumbnail} name={name} />
            </Link>
          ) : (
            <Thumbnail url={thumbnail} name={name} />
          )}
        </BasketItemThumbnailColumn>
        <BasketItemDetailsColumn>
          {!!name && (
            <BasketItemRow isMobile>
              <BasketItemColumn isGrow>
                {linkRoute ? (
                  <Link route={linkRoute} params={linkParams}>
                    <BasketItemTitle>{name}</BasketItemTitle>
                  </Link>
                ) : (
                  <BasketItemTitle>{name}</BasketItemTitle>
                )}
              </BasketItemColumn>
              {isEditable && !hidePrice && (
                <BasketItemColumn>
                  <BasketItemRemoveButton href="#" onClick={handleRemoveClick}>
                    {t('common.remove')}
                  </BasketItemRemoveButton>
                </BasketItemColumn>
              )}
            </BasketItemRow>
          )}

          {name && !hidePrice && (
            <BasketItemRow isDesktop>
              {name ? (
                <BasketItemColumn isGrow>
                  {linkRoute ? (
                    <Link route={linkRoute} params={linkParams}>
                      <BasketItemTitle>{name}</BasketItemTitle>
                    </Link>
                  ) : (
                    <BasketItemTitle>{name}</BasketItemTitle>
                  )}
                </BasketItemColumn>
              ) : (
                <></>
              )}
              {!hidePrice && (
                <>
                  <BasketItemDoubleColumn>
                    <BasketItemPriceWrapper>
                      <DiscountPrice price={price} discount={discount} />
                    </BasketItemPriceWrapper>
                    {isEditable ? (
                      <BasketItemRemoveButton href="#" onClick={handleRemoveClick}>
                        {t('common.remove')}
                      </BasketItemRemoveButton>
                    ) : (
                      <div />
                    )}
                    <BasketItemPricePerNightWrapper>{pricePerNight}</BasketItemPricePerNightWrapper>
                  </BasketItemDoubleColumn>
                </>
              )}
            </BasketItemRow>
          )}
          <MetaWrapper>{metadata}</MetaWrapper>

          <BasketItemRow isMobile>
            {renderDetails && shouldRenderMobileDetails && (
              <BasketItemColumn isGrow>
                <IconWrapper href="#" onClick={handleToggleDetails}>
                  {t('common.moreDetails')}
                  {isDetailsVisible ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconWrapper>
              </BasketItemColumn>
            )}
            {!hidePrice ? (
              <BasketItemPrice>
                <DiscountPrice price={price} discount={discount} />
                {pricePerNight}
              </BasketItemPrice>
            ) : (
              <></>
            )}
          </BasketItemRow>

          {renderDetails && (
            <BasketItemDetailsRow isVisible={isDetailsVisible}>
              {renderDetails}
            </BasketItemDetailsRow>
          )}
        </BasketItemDetailsColumn>
      </BasketItemRow>
    </BasketItemWrapper>
  );
};
